import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type {
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Block } from '@sorare/core/src/atoms/layout/Block';
import { DisplayIfVisible } from '@sorare/core/src/atoms/layout/DisplayIfVisible';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { OpenItemDialogLink } from '@sorare/core/src/components/link/OpenItemDialogLink';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { range } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';

import { PriceHistoryDate } from '#src/components/price/PriceHistoryDate/index.tsx';
import { TransactionLabel } from '#src/components/price/TransactionLabel/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import type { PriceHistoryQuery } from '#src/hooks/__generated__/useGetPriceHistory.graphql.ts';
import { useGetPriceHistory } from '#src/hooks/useGetPriceHistory.ts';

type PriceHistoryQuery_tokens_tokenPrices =
  PriceHistoryQuery['tokens']['tokenPrices'][number];

type PriceHistoryQuery_tokens_tokenPrices_deal =
  PriceHistoryQuery_tokens_tokenPrices['deal'];

type PriceHistoryQuery_tokens_tokenPrices_deal_TokenAuction =
  PriceHistoryQuery_tokens_tokenPrices_deal & { __typename: 'TokenAuction' };

type PriceHistoryQuery_tokens_tokenPrices_deal_TokenAuction_nfts =
  PriceHistoryQuery_tokens_tokenPrices_deal_TokenAuction['cards'][number];

type PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer =
  PriceHistoryQuery_tokens_tokenPrices_deal & { __typename: 'TokenOffer' };

type PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer_senderSide_nfts =
  PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer['senderSide']['cards'][number];

type PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer_receiverSide_nfts =
  PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer['receiverSide']['cards'][number];

const messages = defineMessages({
  noResult: {
    defaultMessage: 'No recent sales',
  },
});

const HistoryList = styled(Vertical).attrs({ gap: 1.5 })`
  overflow: auto;
`;
const SkeletonWrapped = styled(Skeleton)`
  width: 100%;
  height: 88.5px;
  display: block;
`;
const EmptyResult = styled(Block).attrs({ variant: 'round' })`
  padding: var(--intermediate-unit);
  &.compact {
    font: var(--t-12);
    padding: var(--unit);
  }
`;
const ItemWrapper = styled(SBHorizontal).attrs({ gap: 3 })`
  padding: var(--intermediate-unit);
  &.noPadding {
    padding: 0;
  }
`;
const Card = styled.div`
  min-width: calc(5 * var(--unit));
  max-width: calc(5 * var(--unit));
`;
const Amount = styled(BodyM)`
  align-self: center;
  text-align: right;
`;
const Sender = styled(Horizontal)`
  grid-area: sender;
`;

type TransactionToken =
  | PriceHistoryQuery_tokens_tokenPrices_deal_TokenAuction_nfts
  | PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer_senderSide_nfts
  | PriceHistoryQuery_tokens_tokenPrices_deal_TokenOffer_receiverSide_nfts;

type ItemProps = {
  item: { playerSlug: string; rarity: Rarity };
  priceHistorySample: PriceHistoryQuery_tokens_tokenPrices;
  onClick?: (token: TransactionToken) => void;
  inBlock?: boolean;
  showTokenOfferSender?: boolean;
};

const getCardFromDeal = (
  { playerSlug, rarity }: { playerSlug: string; rarity: Rarity },
  deal: PriceHistoryQuery_tokens_tokenPrices_deal
) => {
  if (isType(deal, 'TokenOffer')) {
    return (
      deal.senderSide.cards.find(
        card =>
          card.anyPlayer.slug === playerSlug && card.rarityTyped === rarity
      ) ||
      deal.receiverSide.cards.find(
        card =>
          card.anyPlayer.slug === playerSlug && card.rarityTyped === rarity
      )
    );
  }
  if (isType(deal, 'TokenAuction') || isType(deal, 'TokenPrimaryOffer')) {
    return deal.cards.find(
      card => card.anyPlayer.slug === playerSlug && card.rarityTyped === rarity
    );
  }
  return null;
};

const Item = ({
  item,
  priceHistorySample,
  onClick,
  inBlock,
  showTokenOfferSender,
}: ItemProps) => {
  const card = getCardFromDeal(item, priceHistorySample.deal);
  const { deal } = priceHistorySample;

  if (!card) return null;

  const content = (
    <OpenItemDialogLink
      onClick={() => {
        if (onClick) onClick(card);
      }}
      item={card}
      sport={card.sport}
    >
      <ItemWrapper className={classNames({ noPadding: !inBlock })}>
        <Horizontal>
          <Card>
            <FlexCard card={card} width={80} />
          </Card>
          <Vertical gap={0.5}>
            <LabelM color="var(--c-white)" bold>
              <TransactionLabel deal={priceHistorySample.deal} />
            </LabelM>
            <LabelM color="var(--c-nd-600)">
              <PriceHistoryDate date={priceHistorySample.date} />
            </LabelM>
            {!!showTokenOfferSender &&
              'sender' in deal &&
              isType(deal.sender, 'User') && (
                <Sender gap={0.5}>
                  <Avatar rounded user={deal.sender} size={2} />
                  <Ellipsis>
                    <LabelS color="var(--c-nd-600-opaque)">
                      {deal.sender.nickname}
                    </LabelS>
                  </Ellipsis>
                </Sender>
              )}
          </Vertical>
        </Horizontal>
        <Amount as="div">
          <AmountWithConversion
            monetaryAmount={priceHistorySample.amounts}
            column
            primaryCurrency={Currency.FIAT}
          />
        </Amount>
      </ItemWrapper>
    </OpenItemDialogLink>
  );

  if (inBlock) {
    return (
      <Block noCollapse noPadding>
        {content}
      </Block>
    );
  }

  return content;
};

type Props = {
  count?: number;
  variables: {
    rarity: Rarity;
    playerSlug: string;
    seasonEligibility?: SeasonEligibility;
    from?: Date;
  };
  showTokenOfferSender?: boolean;
} & Omit<ItemProps, 'priceHistorySample' | 'item'>;

const PriceHistoryFromPropsComponent = ({
  variables,
  count,
  onClick,
  inBlock = true,
  showTokenOfferSender,
}: Props) => {
  const { data, loading } = useGetPriceHistory(variables);
  const { tokenPrices } = data?.tokens || {};
  const priceHistory = count ? tokenPrices?.slice(0, count) : tokenPrices;
  const loadingCount = priceHistory?.length || count || 5;
  if (priceHistory?.length === 0 && !loading) {
    return (
      <EmptyResult>
        <FormattedMessage {...messages.noResult} />
      </EmptyResult>
    );
  }

  return (
    <HistoryList>
      {loading
        ? range(loadingCount).map(i => (
            <Block noPadding key={i}>
              <SkeletonWrapped />
            </Block>
          ))
        : priceHistory?.map(priceHistorySample => (
            <Item
              key={priceHistorySample.id}
              priceHistorySample={priceHistorySample}
              onClick={onClick}
              inBlock={inBlock}
              showTokenOfferSender={showTokenOfferSender}
              item={{ ...variables }}
            />
          ))}
    </HistoryList>
  );
};

export const PriceHistoryFromProps = ({
  children,
  ...props
}: PropsWithChildren<Props>) => {
  return (
    <DisplayIfVisible>
      <PriceHistoryFromPropsComponent {...props} />
    </DisplayIfVisible>
  );
};
